import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import { categorizeField, CATEGORIES, sortedCategoryIds, type CategoryId } from "@/utils/payloadCategorization";

/** Small inline "Advanced" badge for advanced fields. */
function AdvancedBadge() {
  return (
    <Badge
      variant="outline"
      className="ml-2 text-[10px] py-0 px-1.5 font-normal text-muted-foreground border-muted-foreground/30"
    >
      Advanced
    </Badge>
  );
}

/** Reusable label that handles required asterisk and advanced badge. */
function FieldLabel({
  label, isRequired, isAdvanced, className,
}: {
  label: string; isRequired?: boolean; isAdvanced?: boolean; className?: string;
}) {
  return (
    <Label className={className ?? "text-sm font-medium"}>
      {label}
      {isRequired && <span className="text-destructive ml-0.5">*</span>}
      {isAdvanced && <AdvancedBadge />}
    </Label>
  );
}

export type FieldFilter = "all" | "modified" | "required";

interface SchemaFormRendererProps {
  schema: Record<string, any>;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  errors?: Record<string, string>;
  path?: string;
  /** Search query applied to field key/title/description (top-level only). */
  search?: string;
  /** Filter chip selection (top-level only). */
  filter?: FieldFilter;
  /** When false, advanced fields (no description, deprecated, x-advanced) are hidden
   *  unless they are required, modified, or surfaced by an active search. */
  showAdvanced?: boolean;
  /** Force-disable categorization (used for nested rendering). */
  disableCategorization?: boolean;
  /** Controlled per-category expanded state. When provided, takes precedence
   *  over the default heuristic. Keys are CategoryId; values are open/closed. */
  openCategories?: Partial<Record<CategoryId, boolean>>;
  /** Called when the user toggles a category. Receives the full updated map. */
  onOpenCategoriesChange?: (next: Partial<Record<CategoryId, boolean>>) => void;
}

const CATEGORIZATION_THRESHOLD = 15;

function isFieldModified(prop: any, value: any): boolean {
  if (value === undefined) return false;
  if (prop && prop.default !== undefined) {
    return JSON.stringify(value) !== JSON.stringify(prop.default);
  }
  // No default → "modified" if value is set to something non-empty
  if (value === null || value === "" ) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (typeof value === "object" && Object.keys(value).length === 0) return false;
  return true;
}

/** Deprecated fields are hidden entirely — they don't function on-device. */
export function isFieldDeprecated(prop: any): boolean {
  return !!(prop && typeof prop === "object" && prop.deprecated === true);
}

/**
 * Heuristic: a field is "advanced" if it lacks a description
 * or carries an `x-advanced` flag. Deprecated fields are filtered out
 * entirely (see `isFieldDeprecated`) and are not surfaced as advanced.
 */
export function isFieldAdvanced(prop: any): boolean {
  if (!prop || typeof prop !== "object") return false;
  if (prop["x-advanced"] === true) return true;
  const desc = typeof prop.description === "string" ? prop.description.trim() : "";
  if (!desc) return true;
  return false;
}

function fieldMatchesSearch(key: string, prop: any, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    key.toLowerCase().includes(q) ||
    (prop?.title || "").toLowerCase().includes(q) ||
    (prop?.description || "").toLowerCase().includes(q)
  );
}

export function SchemaFormRenderer({
  schema,
  values,
  onChange,
  errors = {},
  path = "",
  search = "",
  filter = "all",
  showAdvanced = true,
  disableCategorization = false,
  openCategories,
  onOpenCategoriesChange,
}: SchemaFormRendererProps) {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return <p className="text-sm text-muted-foreground">No configurable properties in this schema.</p>;
  }

  const requiredFields: string[] = schema.required || [];
  const properties = schema.properties as Record<string, any>;
  const entries = Object.entries(properties);

  const handleChange = (key: string, value: any) => {
    onChange({ ...values, [key]: value });
  };

  // Apply search/filter at the top level
  const visibleEntries = entries.filter(([key, prop]) => {
    // Deprecated fields are never rendered — they don't work on-device.
    if (isFieldDeprecated(prop)) return false;
    if (!fieldMatchesSearch(key, prop, search)) return false;
    if (filter === "required" && !requiredFields.includes(key)) return false;
    if (filter === "modified" && !isFieldModified(prop, values[key])) return false;
    // Advanced toggle (independent): hide advanced fields unless required,
    // already modified, or surfaced by an active search.
    if (!showAdvanced && isFieldAdvanced(prop)) {
      const isRequired = requiredFields.includes(key);
      const modified = isFieldModified(prop, values[key]);
      const surfacedBySearch = !!search;
      if (!isRequired && !modified && !surfacedBySearch) return false;
    }
    return true;
  });

  const renderEntry = ([key, prop]: [string, any]) => {
    const fieldPath = path ? `${path}.${key}` : key;
    const isRequired = requiredFields.includes(key);
    const error = errors[fieldPath];
    const label = prop.title || key;
    const description = prop.description;
    const currentValue = values[key];

    return (
      <div key={key} className="space-y-1.5">
        {renderField(
          key, prop, label, description, isRequired, currentValue,
          (v) => handleChange(key, v), error, fieldPath, errors, onChange, values
        )}
      </div>
    );
  };

  const useCategorized =
    !disableCategorization && !path && entries.length > CATEGORIZATION_THRESHOLD;

  if (!useCategorized) {
    if (visibleEntries.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-6">
          No settings match your search.
        </p>
      );
    }
    return <div className="space-y-4">{visibleEntries.map(renderEntry)}</div>;
  }

  // Categorized layout
  const grouped = new Map<CategoryId, Array<[string, any]>>();
  visibleEntries.forEach((entry) => {
    const cat = categorizeField(entry[0]);
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(entry);
  });

  if (grouped.size === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        No settings match your search.
      </p>
    );
  }

  const orderedIds = sortedCategoryIds(Array.from(grouped.keys()));

  const handleCategoryToggle = (catId: CategoryId, nextOpen: boolean) => {
    if (!onOpenCategoriesChange) return;
    onOpenCategoriesChange({ ...(openCategories || {}), [catId]: nextOpen });
  };

  return (
    <div className="space-y-3">
      {orderedIds.map((catId, idx) => {
        const items = grouped.get(catId)!;
        const modifiedCount = items.filter(([k, p]) => isFieldModified(p, values[k])).length;
        // Auto-expand heuristic for groups with no persisted preference:
        // first group, any group with modifications, or any group when searching/filtering.
        const heuristicOpen =
          idx === 0 ||
          modifiedCount > 0 ||
          !!search ||
          filter !== "all";
        const persistedOpen = openCategories?.[catId];
        const open = persistedOpen ?? heuristicOpen;
        return (
          <CategoryGroup
            key={catId}
            id={catId}
            count={items.length}
            modifiedCount={modifiedCount}
            open={open}
            onToggle={(next) => handleCategoryToggle(catId, next)}
          >
            <div className="space-y-4 pt-2">{items.map(renderEntry)}</div>
          </CategoryGroup>
        );
      })}
    </div>
  );
}

function CategoryGroup({
  id, count, modifiedCount, open, onToggle, children,
}: {
  id: CategoryId; count: number; modifiedCount: number;
  open: boolean; onToggle: (next: boolean) => void;
  children: React.ReactNode;
}) {
  const meta = CATEGORIES[id];
  const Icon = meta.icon;

  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-3 h-auto border rounded-lg">
          <div className="flex items-center gap-2 text-left">
            {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">{meta.label}</span>
            <span className="text-xs text-muted-foreground font-normal">
              · {count} setting{count === 1 ? "" : "s"}
              {modifiedCount > 0 && (
                <> · <span className="text-primary font-medium">{modifiedCount} changed</span></>
              )}
            </span>
          </div>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-3 pt-2 ml-3 border-l-2 border-muted">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function renderField(
  key: string,
  prop: any,
  label: string,
  description: string | undefined,
  isRequired: boolean,
  value: any,
  onFieldChange: (v: any) => void,
  error: string | undefined,
  fieldPath: string,
  errors: Record<string, string>,
  onParentChange: (values: Record<string, any>) => void,
  parentValues: Record<string, any>
): React.ReactNode {
  const advanced = isFieldAdvanced(prop);

  // Enum → select
  if (prop.enum) {
    return (
      <div className="space-y-1.5">
        <FieldLabel label={label} isRequired={isRequired} isAdvanced={advanced} />
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <Select value={value?.toString() || ""} onValueChange={onFieldChange}>
          <SelectTrigger><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
          <SelectContent>
            {(prop.enum as any[]).map((opt: any) => (
              <SelectItem key={String(opt)} value={String(opt)}>{String(opt)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  switch (prop.type) {
    case "boolean":
      return (
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="space-y-0.5">
            <FieldLabel label={label} isAdvanced={advanced} />
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
          <Switch checked={!!value} onCheckedChange={onFieldChange} />
        </div>
      );

    case "integer":
    case "number":
      return (
        <div className="space-y-1.5">
          <FieldLabel label={label} isRequired={isRequired} isAdvanced={advanced} />
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <Input
            type="number"
            value={value ?? ""}
            onChange={(e) => onFieldChange(e.target.value === "" ? undefined : Number(e.target.value))}
            min={prop.minimum}
            max={prop.maximum}
            placeholder={prop.default !== undefined ? `Default: ${prop.default}` : undefined}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      );

    case "object":
      return (
        <NestedObjectField
          fieldKey={key}
          prop={prop}
          label={label}
          description={description}
          isAdvanced={advanced}
          value={value || {}}
          onChange={onFieldChange}
          errors={errors}
          fieldPath={fieldPath}
        />
      );

    case "array":
      return (
        <ArrayField
          fieldKey={key}
          prop={prop}
          label={label}
          description={description}
          isRequired={isRequired}
          isAdvanced={advanced}
          value={value || []}
          onChange={onFieldChange}
          errors={errors}
          fieldPath={fieldPath}
        />
      );

    case "string":
    default:
      // Long text
      if (prop.format === "text" || (prop.maxLength && prop.maxLength > 200)) {
        return (
          <div className="space-y-1.5">
            <FieldLabel label={label} isRequired={isRequired} isAdvanced={advanced} />
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
            <Textarea
              value={value || ""}
              onChange={(e) => onFieldChange(e.target.value)}
              placeholder={prop.default !== undefined ? `Default: ${prop.default}` : undefined}
              maxLength={prop.maxLength}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        );
      }

      return (
        <div className="space-y-1.5">
          <FieldLabel label={label} isRequired={isRequired} isAdvanced={advanced} />
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <Input
            value={value || ""}
            onChange={(e) => onFieldChange(e.target.value)}
            placeholder={prop.default !== undefined ? `Default: ${prop.default}` : undefined}
            maxLength={prop.maxLength}
            minLength={prop.minLength}
            pattern={prop.pattern}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      );
  }
}

function NestedObjectField({
  fieldKey, prop, label, description, isAdvanced, value, onChange, errors, fieldPath,
}: {
  fieldKey: string; prop: any; label: string; description?: string; isAdvanced?: boolean;
  value: Record<string, any>; onChange: (v: any) => void;
  errors: Record<string, string>; fieldPath: string;
}) {
  const [open, setOpen] = React.useState(false);

  if (!prop.properties) {
    return (
      <div className="space-y-1.5">
        <FieldLabel label={label} isAdvanced={isAdvanced} />
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <Textarea
          value={typeof value === "object" ? JSON.stringify(value, null, 2) : value || ""}
          onChange={(e) => {
            try { onChange(JSON.parse(e.target.value)); } catch { /* keep raw */ }
          }}
          placeholder="Enter JSON object"
          className="font-mono text-xs"
          rows={4}
        />
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-3 h-auto border rounded-lg">
          <div className="text-left">
            <span className="text-sm font-medium">
              {label}
              {isAdvanced && <AdvancedBadge />}
            </span>
            {description && <p className="text-xs text-muted-foreground font-normal">{description}</p>}
          </div>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4 pt-2 border-l-2 border-muted ml-3">
        <SchemaFormRenderer
          schema={prop}
          values={value}
          onChange={onChange}
          errors={errors}
          path={fieldPath}
          disableCategorization
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

function ArrayField({
  fieldKey, prop, label, description, isRequired, isAdvanced, value, onChange, errors, fieldPath,
}: {
  fieldKey: string; prop: any; label: string; description?: string; isRequired: boolean; isAdvanced?: boolean;
  value: any[]; onChange: (v: any[]) => void;
  errors: Record<string, string>; fieldPath: string;
}) {
  const itemSchema = prop.items;

  const addItem = () => {
    if (!itemSchema) return;
    const defaultItem = itemSchema.type === "object" ? {} : itemSchema.type === "boolean" ? false : "";
    onChange([...value, defaultItem]);
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, newValue: any) => {
    const updated = [...value];
    updated[index] = newValue;
    onChange(updated);
  };

  // Simple string/number arrays
  if (itemSchema && (itemSchema.type === "string" || itemSchema.type === "number" || itemSchema.type === "integer")) {
    // If items have enum, use multi-select style
    if (itemSchema.enum) {
      return (
        <div className="space-y-1.5">
          <FieldLabel label={label} isRequired={isRequired} isAdvanced={isAdvanced} />
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
          <div className="flex flex-wrap gap-2">
            {(itemSchema.enum as any[]).map((opt: any) => {
              const selected = value.includes(opt);
              return (
                <Button
                  key={String(opt)}
                  type="button"
                  variant={selected ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (selected) onChange(value.filter((v) => v !== opt));
                    else onChange([...value, opt]);
                  }}
                >
                  {String(opt)}
                </Button>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-1.5">
        <FieldLabel label={label} isRequired={isRequired} isAdvanced={isAdvanced} />
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <div className="space-y-2">
          {value.map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item || ""}
                onChange={(e) => updateItem(index, itemSchema.type === "number" ? Number(e.target.value) : e.target.value)}
                type={itemSchema.type === "number" || itemSchema.type === "integer" ? "number" : "text"}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-4 w-4 mr-1" /> Add item
          </Button>
        </div>
      </div>
    );
  }

  // Object arrays
  if (itemSchema && itemSchema.type === "object") {
    return (
      <div className="space-y-1.5">
        <FieldLabel label={label} isRequired={isRequired} isAdvanced={isAdvanced} />
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <div className="space-y-3">
          {value.map((item, index) => (
            <div key={index} className="border rounded-lg p-3 relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={() => removeItem(index)}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
              <p className="text-xs font-medium text-muted-foreground mb-2">Item {index + 1}</p>
              <SchemaFormRenderer
                schema={itemSchema}
                values={item || {}}
                onChange={(v) => updateItem(index, v)}
                errors={errors}
                path={`${fieldPath}[${index}]`}
                disableCategorization
              />
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            <Plus className="h-4 w-4 mr-1" /> Add {itemSchema.title || "item"}
          </Button>
        </div>
      </div>
    );
  }

  // Fallback: JSON textarea
  return (
    <div className="space-y-1.5">
      <FieldLabel label={label} isAdvanced={isAdvanced} />
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <Textarea
        value={JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value)); } catch { /* keep raw */ }
        }}
        className="font-mono text-xs"
        rows={4}
      />
    </div>
  );
}

/** Counts total top-level fields, how many are modified, and how many are
 *  candidates to be hidden by the "Show advanced" toggle (advanced AND not
 *  required AND not modified). */
export function countSchemaFields(
  schema: Record<string, any> | null | undefined,
  values: Record<string, any>
): { total: number; modified: number; advancedHidden: number } {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return { total: 0, modified: 0, advancedHidden: 0 };
  }
  // Deprecated fields are not rendered, so exclude them from all counts.
  const entries = Object.entries(schema.properties as Record<string, any>)
    .filter(([, p]) => !isFieldDeprecated(p));
  const required: string[] = Array.isArray(schema.required) ? schema.required : [];
  const total = entries.length;
  let modified = 0;
  let advancedHidden = 0;
  for (const [k, p] of entries) {
    const isMod = isFieldModified(p, values[k]);
    if (isMod) modified++;
    if (isFieldAdvanced(p) && !required.includes(k) && !isMod) advancedHidden++;
  }
  return { total, modified, advancedHidden };
}
